import { useEffect, useRef, useState } from 'react';
import { Chip } from '@nextui-org/react';
import CtyPromptInput, { promptInputType } from '@circuitry-ai/doc-ui';
import { useTranslation } from 'react-i18next';
import type { Blueprint, ChatTurn } from './agentBuilderApi';

// Left column of the builder. The first message describes the journey and builds its workflow;
// every later message is a change request applied to the workflow on the right. When a message is
// too vague (or not this kind of journey), the builder asks a question with answers to pick.
export function BuilderChat({
  turns,
  built,
  busy,
  error,
  blueprint,
  onSend,
  onUndo,
}: {
  turns: ChatTurn[];
  built: boolean;
  busy: boolean;
  error: string | null;
  blueprint: Blueprint | null;
  onSend: (text: string) => void;
  onUndo: () => void;
}) {
  const { t } = useTranslation();
  const [pending, setPending] = useState<string | null>(null);
  const end = useRef<HTMLDivElement | null>(null);
  const waiting = busy ? pending : null;

  useEffect(() => {
    end.current?.scrollIntoView({ block: 'end' });
  }, [turns.length, waiting]);

  const submit = (value: string) => {
    if (!value.trim() || busy) return;
    setPending(value.trim());
    onSend(value.trim());
  };
  const lastIndex = turns.length - 1;

  return (
    <section className="flex flex-col min-h-0 h-full bg-content1" aria-label={t('Builder chat')} data-testid="ab-chat">
      <div className="flex-1 min-h-0 overflow-y-auto p-3 flex flex-col gap-2">
        {turns.length === 0 && !waiting && built && (
          <p className="text-sm text-default-500">
            {t('Ask for a change in plain words, for example: "Create a ticket when the customer asks for a refund."')}
          </p>
        )}
        {turns.length === 0 && !waiting && !built && (
          <div className="text-sm text-default-600 flex flex-col gap-2">
            <p className="font-semibold text-foreground">{t('Describe what this journey should do.')}</p>
            {blueprint ? (
              <>
                <p>
                  {t('It starts as a {{title}}: built from that blueprint, and you can add any other action as you go.', { title: blueprint.title })} {blueprint.description}
                </p>
                <button
                  type="button"
                  className="rounded-lg border border-default-200 p-2 text-left hover:bg-default-100"
                  onClick={() => submit(blueprint.example_requirement)}
                  disabled={busy}
                  data-testid="ab-use-standard"
                >
                  <span className="font-semibold text-foreground">{t('Start from the standard {{title}}', { title: blueprint.title })}</span>
                  <span className="block text-xs">{blueprint.example_requirement}</span>
                </button>
              </>
            ) : (
              <p>{t('The workflow appears on the right, built only from the tools the agent platform offers.')}</p>
            )}
            <p>{t('Then ask for changes here, in plain words.')}</p>
          </div>
        )}
        {turns.map((turn, i) => (
          <div key={i} className={`flex max-w-[92%] flex-col gap-1 ${turn.role === 'user' ? 'self-end' : 'self-start'}`}>
            <div
              data-testid={`ab-chat-${turn.role}`}
              className={`whitespace-pre-wrap rounded-xl px-3 py-2 text-sm ${
                turn.role === 'user'
                  ? 'bg-primary text-primary-foreground rounded-br-sm'
                  : `border bg-content2 rounded-bl-sm ${turn.error ? 'border-warning text-warning' : 'border-default-200'}`
              }`}
            >
              {turn.text}
            </div>
            {!!turn.options?.length && (
              <div className="flex flex-wrap gap-1" data-testid="ab-chat-options">
                {turn.options.map((option) => (
                  <Chip
                    key={option}
                    as="button"
                    variant={i === lastIndex ? 'bordered' : 'flat'}
                    color="primary"
                    classNames={{ base: 'h-auto max-w-full py-1', content: 'whitespace-normal text-left' }}
                    className={i === lastIndex && !busy ? 'cursor-pointer' : 'opacity-60'}
                    onClick={() => i === lastIndex && submit(option)}
                  >
                    {option}
                  </Chip>
                ))}
              </div>
            )}
            {turn.undo && i === lastIndex && (
              <Chip
                as="button"
                variant="light"
                size="sm"
                color="default"
                className={busy ? 'opacity-60' : 'cursor-pointer'}
                onClick={() => !busy && onUndo()}
                data-testid="ab-chat-undo"
              >
                ↶ {t('Undo this change')}
              </Chip>
            )}
          </div>
        ))}
        {waiting && (
          <>
            <div className="max-w-[92%] self-end whitespace-pre-wrap rounded-xl rounded-br-sm bg-primary px-3 py-2 text-sm text-primary-foreground">{waiting}</div>
            <div className="max-w-[92%] self-start animate-pulse rounded-xl rounded-bl-sm border border-default-200 bg-content2 px-3 py-2 text-sm text-default-500" role="status">
              {built ? t('Updating the workflow…') : t('Building the workflow…')} {t('This usually takes 20 to 40 seconds.')}
            </div>
          </>
        )}
        {error && !busy && <div className="max-w-[92%] self-start rounded-xl border border-warning px-3 py-2 text-sm text-warning">{error}</div>}
        <div ref={end} />
      </div>
      {/* The same prompt input as the advisor chat (chatForm.tsx). */}
      <div className="p-2">
        <div className="chat-converse-input relative flex w-full flex-col items-start rounded-medium border bg-content1">
          <CtyPromptInput
            elementType={promptInputType.TEXTAREA}
            id="ab-chat-input"
            data-testid="ab-chat-input"
            classNames={{
              inputWrapper: '!bg-transparent shadow-none',
              innerWrapper: 'relative',
              input: 'pt-1 pb-6 pl-2 !pr-10 text-medium',
            }}
            radius="lg"
            variant="flat"
            placeholder={built ? t('Ask for a change…') : t('Describe what this journey should do…')}
            handleSendClick={(value: string) => submit(value)}
            enableSendBtn
            isDisabled={busy}
          />
        </div>
      </div>
    </section>
  );
}
